import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search, Edit2, Trash2, MapPin, Phone } from 'lucide-react'
import api from '../../services/api'
import { formatPhone } from '../../utils/formatters'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import ConfirmDialog from '../../components/ConfirmDialog'
import toast from 'react-hot-toast'

// Busca destinatários
const fetchRecipients = async (search = '') => {
  const response = await api.get('/api/recipients', {
    params: { search, limit: 50 }
  })
  return response.data.data
}

// Texto de endereço: usa address.full quando disponível, senão monta a partir dos campos crus (legado)
const renderRecipientAddress = (recipient) => {
  const full = recipient.address?.full
  if (full) return full

  const street = recipient.address?.street || recipient.addressStreet
  const number = recipient.address?.number || recipient.addressNumber
  const neighborhood = recipient.address?.neighborhood || recipient.addressNeighborhood
  const city = recipient.address?.city || recipient.addressCity
  const reference = recipient.address?.reference || recipient.addressReference

  if (street || number || neighborhood || city) {
    return (
      <>
        {street}, {number} - {neighborhood}, {city}
        {reference && ` (${reference})`}
      </>
    )
  }

  return 'Endereço pendente'
}

export default function RecipientsList() {
  const [search, setSearch] = useState('')
  const [dialogState, setDialogState] = useState(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['recipients', search],
    queryFn: () => fetchRecipients(search),
  })

  const handleDeleteClick = (id, name) => {
    setDialogState({ id, name })
  }

  const handleConfirmDelete = async () => {
    const { id } = dialogState
    setDialogState(null)

    try {
      await api.delete(`/api/recipients/${id}`)
      toast.success('Destinatário excluído')
      refetch()
    } catch (error) {
      toast.error('Erro ao excluir destinatário')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Destinatários</h1>
          <p className="page-subtitle">Gerencie seus clientes e endereços de entrega.</p>
        </div>
        <Link to="/recipients/new">
          <Button>
            <Plus className="w-4 h-4" />
            Novo Destinatário
          </Button>
        </Link>
      </div>

      {/* Busca */}
      <Card padding="sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome, telefone ou endereço..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-0 focus:ring-0 text-gray-900 placeholder-gray-400"
          />
        </div>
      </Card>

      {/* Lista */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : data?.recipients?.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Nenhum destinatário encontrado</p>
            <Link to="/recipients/new">
              <Button variant="outline" size="sm">
                <Plus className="w-4 h-4" />
                Adicionar primeiro destinatário
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {data?.recipients?.map((recipient) => (
              <div 
                key={recipient.id}
                className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-700 font-medium text-lg">
                      {recipient.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{recipient.name}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {formatPhone(recipient.phone)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {renderRecipientAddress(recipient)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/recipients/${recipient.id}/edit`}>
                    <button className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </Link>
                  <button
                    onClick={() => handleDeleteClick(recipient.id, recipient.name)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!dialogState}
        title="Excluir destinatário"
        message={dialogState ? `Tem certeza que deseja excluir "${dialogState.name}"?` : ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDialogState(null)}
      />
    </div>
  )
}
